<!--
 * @Description: 加入企业申请审批
 * @Author: Xiaochun liu
 * @Date: 2024-04-12 14:41:37
 * @LastEditors: Lxc 1533761120@qq.com
 * @LastEditTime: 2024-05-11 15:54:44
-->
<script setup lang="ts">
import { useMessage } from 'naive-ui'
import { Close } from '@vicons/carbon'
import { applyEnterpriseReviewHasPermissions } from '@/api/message-notify'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
})
const emits = defineEmits(['update:modelValue', 'updateHandle'])

const message = useMessage()

const manageValue = ref(0)
const editValue = ref(null)
watch(() => manageValue, () => {
  if (manageValue.value === 0) {
    editValue.value = null
  }
}, { deep: true })

const showModal = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emits('update:modelValue', value)
  },
})

function closeHandle() {
  showModal.value = false
}

const limitVisible = ref(false)
const errorText = ref('')
function submitHandle() {
  if (manageValue.value === 1 && editValue.value === null) {
    errorText.value = '请选择编辑权限'
    return
  }
  applyEnterpriseReviewHasPermissions({
    applyId: props.data.applyId,
    code: 1,
    enterpriseId: props.data.enterpriseId,
    memberId: props.data.userId,
    permissions: manageValue.value === 0 ? [] : editValue.value,
  }).then((res) => {
    window.$message?.success(`你通过了${props.data.nickName}的加入申请`)
    closeHandle()
    emits('updateHandle')
  }).catch((error) => {
    if (error.code === 90003) {
      limitVisible.value = true
    } else {
      message.error(error.resultMsg)
    }
  })
}
</script>

<template>
  <NModal v-model:show="showModal">
    <div class="apply-for-enterprise-dialog">
      <div class="dialog-header flex items-center justify-between">
        <div class="header-title">
          <div class="title">
            加入企业申请审批
          </div>
          <div class="sub-title">
            请给<span class="high-text">{{ data.nickName }}</span>分配企业管理权限
          </div>
        </div>
        <button class="close-btn" :data-ph-capture-attribute-page-actions="$getPosthogPath(['通过', '加入企业申请审批', '取消'])"
          @click="closeHandle">
          <NIcon :size="20" :component="Close" />
        </button>
      </div>
      <div class="dialog-body">
        <div class="manage-tip">
          只有分配权限并且点击下方“确定”按钮之后才会成功通过申请
        </div>
        <div class="manage-wrap">
          <div class="authority-title">
            管理权限
          </div>
          <NRadioGroup v-model:value="manageValue" name="radiogroup">
            <NSpace>
              <NRadio :value="0">
                无管理权限
              </NRadio>
              <NRadio :value="1">
                有管理权限
              </NRadio>
            </NSpace>
          </NRadioGroup>
        </div>
        <div class="edit-wrap">
          <div class="authority-title">
            编辑权限
          </div>
          <NCheckboxGroup v-model:value="editValue">
            <NSpace item-style="display: flex;">
              <NCheckbox :value="1" label="管理企业知识" :disabled="manageValue === 0" />
              <NCheckbox :value="2" label="创建产品" :disabled="manageValue === 0" />
            </NSpace>
          </NCheckboxGroup>
          <div class="error-text">
            {{ errorText }}
          </div>
        </div>
      </div>
      <div class="dialog-footer flex justify-end">
        <button class="button-gradient submit-btn"
          :data-ph-capture-attribute-page-actions="$getPosthogPath(['通过', '加入企业申请审批', '确定'])" @click="submitHandle">
          确定
        </button>
      </div>
    </div>
  </NModal>
  <!-- 达到上限弹窗 -->
  <LimitDialog v-model="limitVisible" dialog-title="成员申请审核">
    <template #default>
      <div class="tip text-[14px] text-[#F53F3F]">
        通过失败，空间成员数量已达上限
      </div>
      <div class="tip text-[#425466]">
        您可联系下方客服扩充空间成员数量
      </div>
    </template>
  </LimitDialog>
</template>

<style lang="scss" scoped>
.apply-for-enterprise-dialog {
  width: 500px;
  height: 369px;
  padding: 24px;
  background: #FFFFFF;
  border-radius: 8px;

  .dialog-header {
    position: relative;
    margin-bottom: 8px;

    .header-title {
      .title {
        margin-bottom: 8px;
        font-weight: 500;
        font-size: 16px;
        color: #242F57;
        line-height: 22px;
      }

      .sub-title {
        font-size: 12px;
        color: #425466;
        line-height: 20px;

        .high-text {
          color: #FF4080;
        }
      }
    }

    .close-btn {
      position: absolute;
      top: -8px;
      right: -8px;
      z-index: 1;
    }
  }

  .dialog-body {
    .manage-tip {
      width: 100%;
      padding: 8px 12px;
      margin-bottom: 12px;
      background: rgba(255, 250, 232, 0.91);
      border-radius: 4px;
      font-size: 12px;
      color: #C8831D;
      line-height: 20px;
    }

    .authority-title {
      margin-bottom: 14px;
      font-weight: 500;
      font-size: 14px;
      color: #242F57;
      line-height: 23px;
    }

    .manage-wrap {
      margin-bottom: 36px;

      :deep(.n-radio) {
        --n-dot-color-active: #FF4080 !important;
        --n-box-shadow-active: inset 0 0 0 1px #FF4080 !important;
        --n-box-shadow-focus: inset 0 0 0 1px #FF4080, 0 0 0 2px rgba(255, 64, 128, 0.2) !important;
        --n-box-shadow-hover: inset 0 0 0 1px #FF4080 !important;
      }
    }

    .edit-wrap {
      position: relative;
      margin-bottom: 27px;

      :deep(.n-checkbox) {
        --n-border-checked: 1px solid #FF4080 !important;
        --n-border-focus: 1px solid #FF4080 !important;
        --n-box-shadow-focus: 0 0 0 2px rgba(255, 64, 128, 0.3) !important;
        --n-color-checked: #FF4080 !important;
      }

      .error-text {
        position: absolute;
        bottom: -20px;
        left: 0;
        color: #FF4080;
      }
    }
  }

  .dialog-footer {
    .submit-btn {
      width: 92px;
      height: 32px;
      font-size: 14px;
    }
  }
}
</style>
